import Vue from 'vue';
import Router from 'vue-router';
import store from './store';

import { NotFound, ReactDashboard } from '@/views';
// import MetricSchmea from '@/views/MetricSchema.vue';

import { from as fromPromise } from 'rxjs';
import { timeout } from 'rxjs/operators';

import { stringify } from 'qs';

import { ACCOUNT_TYPES, MFA, PP_KEY } from '@consts';

import { loadIconCss } from './utils/loadIconCss';

import { getAdrielJwt, setAdrielJwt, isValueExist } from '@util';
import { AuthService } from './services';
import { requireMfaChallenge } from '@/utils/mfaAuth';
import { isSignRoute, normalizeSignupQuery } from './utils/signup';

const floatingAccountVisible = {
    floatingAccountVisible: false,
};

const canChangeAccount = {
    canChangeAccount: false,
};

Vue.use(Router);

// Every login lands on MyCampaigns, which forwards Plan T workspaces to the
// dashboard once the workspace has loaded (its `account.account_type`
// watcher). Right after a login the workspace is still empty, so the
// campaigns page is drawn and swapped a moment later (QA 2026-10-26). Wait
// for the workspace here, before the view mounts; if it does not arrive in
// time, fall through and let the view's watcher handle it as before.
// Temporary: goes away with the Plan A sunset, see
// docs/fix/2026-10-26-login-landing-flash.md.
const WORKSPACE_WAIT_MS = 3000;

const waitForPlanType = () =>
    new Promise((resolve) => {
        const read = () => store.getters['user/planType'];
        if (read()) return resolve();

        let unwatch;
        const timer = setTimeout(() => {
            unwatch();
            resolve();
        }, WORKSPACE_WAIT_MS);

        unwatch = store.watch(read, (type) => {
            if (!type) return;
            clearTimeout(timer);
            unwatch();
            resolve();
        });
    });

// Same condition as MyCampaigns.vue (beforeMount / account_type watcher).
const goesToDashboard = () =>
    store.getters['user/planType'] === ACCOUNT_TYPES.PLAN_T &&
    !R.path(['meta', 'hideDashboard'], store.getters['user/account']);

const DASHBOARD = {
    path: '/dashboard/:id?',
    name: 'Dashboard',
    meta: {
        toTopVisible: false,
        gnbVisible: false,
        requireLogin: true,
        needSubscription: true,
        showCurrencyModal: true,
    },
    component: ReactDashboard,
};

const router = new Router({
    routes: [
        {
            path: '/',
            redirect: (data = {}) => {
                const query = R.prop({}, 'query', data);
                return {
                    path: '/myCampaigns',
                    query: { ...query, redirected: 1 },
                };
            },
        },
        {
            path: '/form',
            name: 'Form',
            component: () =>
                import(
                    /* webpackChunkName: "formResult" */ '@/components/Forms/FormResult'
                ),
        },
        {
            path: '/dataTransfer',
            name: 'DataTransfer',
            meta: {
                ...canChangeAccount,
                gnbVisible: true,
                requireLogin: true,
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "dataTransfer" */ '@/views/ExportData/ExportData'
                ),
        },
        {
            path: '/dataTransfer/apiCenter',
            name: 'ApiCenter',
            meta: {
                ...canChangeAccount,
                gnbVisible: true,
                requireLogin: true,
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "apiCenter" */ '@/views/ReactApiCenter'
                ),
        },
        {
            path: '/onboarding/:id?',
            name: 'Onboarding',
            meta: { ...floatingAccountVisible, requireLogin: true },
            component: () =>
                import(
                    /* webpackChunkName: "onboarding" */ '@/views/Onboarding'
                ),
        },
        {
            path: '/sign',
            component: () =>
                import(/* webpackChunkName: "sign" */ '@/views/Sign.js'),
            meta: floatingAccountVisible,
            children: [
                {
                    path: 'login',
                    meta: {
                        ...floatingAccountVisible,
                        toTopVisible: false,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "sign" */ '@/components/Sign/Login.js'
                        ),
                    name: 'Sign/Login',
                },
                {
                    path: 'join',
                    meta: {
                        ...floatingAccountVisible,
                        toTopVisible: false,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "sign" */ '@/components/Sign/Join.js'
                        ),
                    name: 'Sign/Join',
                },
                {
                    path: 'mfa',
                    meta: {
                        ...floatingAccountVisible,
                        toTopVisible: false,
                    },
                    beforeEnter: requireMfaChallenge(store),
                    component: () =>
                        import(
                            /* webpackChunkName: "sign" */ '@/components/Sign/Mfa/MfaChallenge.js'
                        ),
                    name: MFA.ROUTE_NAME,
                },
                {
                    // Absolute path: the server redirects social logins to
                    // `/#/oauth-success`. Nesting it here keeps the sign-in
                    // layout on screen while the login is verified.
                    path: '/oauth-success',
                    name: 'OauthSuccess',
                    meta: {
                        requireLogin: false,
                        toTopVisible: false,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "oauth_success" */ '@/views/Redirect'
                        ),
                },
                {
                    path: 'splash',
                    meta: {
                        ...floatingAccountVisible,
                        toTopVisible: false,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "sign" */ '@/components/MobileOnboarding/Splash.vue'
                        ),
                    name: 'Sign/Splash',
                },
            ],
        },
        {
            path: '/sign/forgot',
            name: '/Sign/Forgot',
            meta: { ...floatingAccountVisible, toTopVisible: false },
            component: () =>
                import(
                    /* webpackChunkName: "sign" */ '@components/Sign/Forgot.js'
                ),
        },
        // {
        //     path: '/sign/verify',
        //     name: '/Sign/Verify',
        //     meta: { ...floatingAccountVisible, toTopVisible: false },
        //     component: () =>
        //         import(
        //             /* webpackChunkName: "sign" */ '@components/Sign/Verify.js'
        //         ),
        // },
        {
            path: '/sign/reset',
            meta: { ...floatingAccountVisible, toTopVisible: false },
            component: () =>
                import(
                    /* webpackChunkName: "sign" */ '@/components/Sign/Reset.js'
                ),
            name: 'Sign/Reset',
        },
        {
            path: '/proposal/:id',
            component: () =>
                import(/* webpackChunkName: "proposal" */ '@/views/Proposal'),
            children: [
                {
                    path: 'form',
                    component: () =>
                        import(
                            /* webpackChunkName: "proposal" */ '@/components/Onboarding/Form'
                        ),
                    name: 'Proposal/Form',
                    meta: { gnbVisible: true, requireLogin: true },
                },
                {
                    path: 'ads',
                    meta: {
                        ...canChangeAccount,
                        gnbVisible: true,
                        requireLogin: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "proposal" */ '@/components/ads/Ads'
                        ),
                    name: 'Proposal/Ads',
                },
                {
                    path: 'plan',
                    meta: {
                        ...canChangeAccount,
                        gnbVisible: true,
                        requireLogin: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "proposal" */ '@/components/Proposal/Plan'
                        ),
                    name: 'Proposal/Plan',
                },
                {
                    path: 'audienceProfile',
                    meta: {
                        ...canChangeAccount,
                        gnbVisible: true,
                        requireLogin: true,
                    },
                    component: () =>
                        import(
                            /* webpackChunkName: "proposal" */ '@/components/Proposal/AudienceProfile'
                        ),
                    name: 'Proposal/AudienceProfile',
                },
                { path: '*', redirect: 'ads' },
                { path: '', redirect: 'ads' },
            ],
        },
        {
            path: '/launched',
            meta: { gnbVisible: true },
            name: 'CampaignLaunched',
            component: () =>
                import(
                    /* webpackChunkName: "proposal" */ '@/components/Proposal/AfterLaunch.vue'
                ),
        },
        {
            path: '/inReview',
            meta: { gnbVisible: true },
            name: 'InReview',
            component: () =>
                import(
                    /* webpackChunkName: "proposal" */ '@/components/Proposal/InReview.vue'
                ),
        },
        {
            path: '/myCampaigns',
            name: 'MyCampaigns',
            component: () =>
                import(
                    /* webpackChunkName: "my_campaigns" */ '@/views/MyCampaigns.vue'
                ),
            meta: { isRefresh: false, gnbVisible: true, requireLogin: true },
            beforeEnter: async (to, from, next) => {
                to.meta.isRefresh = from.name === null;

                await waitForPlanType();

                if (goesToDashboard()) {
                    return next({
                        name: 'Dashboard',
                        query: to.query,
                        replace: true,
                    });
                }
                next();
            },
        },
        {
            path: '/settings/:tab?/:page?',
            name: 'Settings',
            meta: {
                gnbVisible: true,
                requireLogin: true,
                canChangeAccount: true,
                toTopVisible: false,
                hideChatbot: true,
            },
            component: () => import('@/views/ReactSettings.js'),
        },

        {
            path: '/billingHistory',
            name: 'BillingHistory',
            meta: { gnbVisible: true },
            component: () => import('@/views/ReactBillingHistory'),
        },
        {
            path: '/billingHistoryOld',
            meta: { gnbVisible: true },
            name: 'BillingHistoryOld',
            component: () =>
                import(
                    /* webpackChunkName: "billingHistoryOld" */ '@/views/BillingHistory.vue'
                ),
        },
        {
            path: '/helpCampaign',
            name: 'HelpCampaign',
            meta: { gnbVisible: true },
            component: () =>
                import(
                    /* webpackChunkName: "helpCampaign" */ '@/views/HelpCampaign.js'
                ),
        },
        DASHBOARD,
        {
            path: '/dashboard/shared/:id',
            name: 'DashboardSharedView',
            meta: {
                toTopVisible: false,
                fetchUser: false,
            },

            component: () => import('@/views/ReactDashboardSharedView.js'),
        },
        {
            path: '/dashboard/sharedTeamspace/:id',
            name: 'DashboardSharedTeamspace',
            meta: {
                toTopVisible: false,
                fetchUser: false,
            },

            component: () => import('@/views/ReactDashboardSharedView.js'),
        },
        {
            path: '/dashboard/sharedFolder/:id',
            name: 'DashboardSharedFolder',
            meta: {
                headerVisible: false,
                gnbVisible: false,
                toTopVisible: false,
                fetchUser: false,
            },

            component: () => import('@/views/ReactDashboardSharedView.js'),
        },
        {
            path: '/dashboard/customizable/alarms',
            name: 'Alarms',
            meta: {
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "alarms" */ '@/views/ReactAutomation'
                ),
        },
        {
            path: '/manager/:category?',
            name: 'Manager',
            redirect: '/',
        },
        {
            path: '/express',
            name: 'Express',
            redirect: '/',
        },
        {
            path: '/mobileDemo',
            name: 'MobileDemo',
            meta: { requireLogin: true },
            component: () =>
                import(
                    /* webpackChunkName: "express" */ '@/components/MobileOnboarding/Demo.js'
                ),
        },
        {
            path: '/express/proposal',
            name: 'New/Proposal',
            component: () =>
                import(
                    /* webpackChunkName: "express" */ '@/views/proposal/index.js'
                ),
        },
        {
            path: '/404',
            name: 'NotFound',
            component: NotFound,
        },
        {
            path: '/empty',
            meta: { gnbVisible: true },
            component: () =>
                import(/* webpackChunkName: "empty" */ '@/views/Empty.vue'),
        },
        {
            path: '/demoRedirection',
            component: () =>
                import(
                    /* webpackChunkName: "demo" */ '@/views/DemoRedirection.vue'
                ),
        },
        {
            path: '/businessLink',
            name: 'BusinessLink',
            meta: { ...floatingAccountVisible },
            component: () =>
                import(
                    /* webpackChunkName: "businessLink" */ '@/views/BusinessLink.js'
                ),
        },
        {
            path: '/welcomeSelection',
            name: 'WelcomeSelection',
            component: () =>
                import(
                    /* webpackChunkName: "express" */ '@/views/WelcomeSelection.js'
                ),
        },
        { path: '*', redirect: '/404' },
        {
            path: '/connection',
            name: 'Connection',
            component: () =>
                import(
                    /* webpackChunkName: "connection" */ '@/views/Connection'
                ),
            meta: {
                requireLogin: true,
                showCurrencyModal: true,
                toTopVisible: false,
            },
        },
        {
            path: '/connection/source-refresh-history',
            name: 'ConnectionSourceRefreshHistory',
            component: () =>
                import(
                    /* webpackChunkName: "connectionRefreshHistory" */ '@/views/ReactConnectionSourceRefreshHistory'
                ),
            meta: {
                requireLogin: true,
                showCurrencyModal: true,
                toTopVisible: false,
            },
        },
        {
            path: '/emptyconnection',
            name: 'EmptyConnectionPanelPage',
            meta: {},
            component: () =>
                import(
                    '@/views/EmptyConnectionPanelPage/EmptyConnectionPanelPage'
                ),
        },
        {
            path: '/white-label-settings',
            name: 'WhiteLabelSettings',
            meta: { gnbVisible: true },
            component: () => import('@/views/ReactWhiteLabelSettings'),
        },
        {
            path: '/scheduled-exports',
            name: 'ManagedScheduledExports',
            meta: { gnbVisible: true },
            component: () => import('@/views/ReactManagedScheduledExports'),
        },
        {
            path: '/emptyWs',
            name: 'EmptyWs',
            meta: { gnbVisible: false, ...canChangeAccount },
            component: () => import('@/views/EmptyWs'),
        },
        {
            path: '/quickconnect',
            name: 'QuickConnect',
            meta: { requireLogin: false, toTopVisible: false },
            component: () =>
                import(
                    /* webpackChunkName: "material" */ '@/views/QuickConnect.js'
                ),
        },
        {
            path: '/material',
            component: () =>
                import(
                    /* webpackChunkName: "material" */ '@/views/Material.js'
                ),
            meta: { gnbVisible: true },
            children: [
                {
                    path: 'form',
                    meta: { ...canChangeAccount, gnbVisible: true },
                    component: () =>
                        import(
                            /* webpackChunkName: "material" */ '@/components/material/Form.js'
                        ),
                    name: 'Material/Form',
                },
                {
                    path: 'invoice',
                    meta: { ...canChangeAccount, gnbVisible: true },
                    component: () =>
                        import(
                            /* webpackChunkName: "material" */ '@/components/material/Invoice.js'
                        ),
                    name: 'Material/Invoice',
                },
                {
                    path: 'pay',
                    meta: { ...canChangeAccount, gnbVisible: true },
                    component: () =>
                        import(
                            /* webpackChunkName: "material" */ '@/components/material/Pay.js'
                        ),
                    name: 'Material/Pay',
                },
                {
                    path: 'finish',
                    meta: { ...canChangeAccount, gnbVisible: true },
                    component: () =>
                        import(
                            /* webpackChunkName: "material" */ '@/components/material/Finish.js'
                        ),
                    name: 'Material/Finish',
                },
            ],
        },
        {
            path: '/building',
            name: 'Building',
            meta: { requireLogin: true, toTopVisible: false, gnbVisible: true },
            component: () =>
                import(
                    /* webpackChunkName: "material" */ '@/views/csmService/Building'
                ),
        },
        {
            path: '/consulting',
            name: 'Consulting',
            meta: { requireLogin: true, toTopVisible: false, gnbVisible: true },
            component: () =>
                import(
                    /* webpackChunkName: "material" */ '@/views/csmService/Consulting'
                ),
        },
        {
            path: '/503',
            component: () =>
                import(
                    /* webpackChunkName: "maintenance" */ '@/views/EmergencyPage'
                ),
            name: 'EmergencyPage',
        },
        {
            path: '/auth',
            name: 'Auth',
            component: () => import('@/views/Auth/Email'),
        },
        {
            path: '/sign/verify',
            name: 'Verify',
            component: () => import('@/views/Auth/Result'),
        },
        {
            path: '/metric-schema',
            name: 'MetricSchema',
            // component: MetricSchmea,
            component: () =>
                import(
                    /* webpackChunkName: "metric_schema" */ '@/views/MetricSchema.vue'
                ),
            meta: { requireLogin: false, fetchUser: false },
        },
        {
            path: '/workspace',
            name: 'CreateWorkspace',
            meta: {
                requireLogin: true,
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "maintenance" */ '@/views/Workspace/CreateWorkspace'
                ),
        },
        {
            path: '/sm',
            name: 'SM',
            meta: {
                toTopVisible: false,
            },
            component: () => import(/* webpackChunkName: "sm" */ '@/views/Sm'),
        },
        {
            path: '/agent/chat/:id?',
            name: 'AgentChat',
            meta: {
                requireLogin: true,
                toTopVisible: false,
                canChangeAccount: true,
            },
            component: () =>
                import(
                    /* webpackChunkName: "agentChat" */ '@/views/ReactAgentChat.js'
                ),
        },
        {
            path: '/custom-exchange-rate',
            name: 'CustomExchangeRate',
            meta: {
                gnbVisible: true,
                requireLogin: true,
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "customExchangeRate" */ '@/views/ReactExchangeRateSetting.js'
                ),
        },
        {
            path: '/margin-management',
            name: 'MarginManagement',
            meta: {
                gnbVisible: true,
                requireLogin: true,
                toTopVisible: false,
            },
            component: () =>
                import(
                    /* webpackChunkName: "marginManagement" */ '@/views/ReactMarginManagement.js'
                ),
        },
        {
            path: '/teamspaces',
            name: 'Teamspaces',
            meta: {
                gnbVisible: false,
                requireLogin: true,
                toTopVisible: false,
                hideChatbot: true,
            },
            component: () =>
                import(
                    /* webpackChunkName: "teamspaces" */ '@/views/ReactTeamspaces.js'
                ),
        },
        {
            path: '/teamspaces/settings',
            name: 'TeamspaceSettings',
            meta: {
                gnbVisible: false,
                requireLogin: true,
                toTopVisible: false,
                hideChatbot: true,
            },
            component: () =>
                import(
                    /* webpackChunkName: "teamspaces" */ '@/views/ReactTeamspaces.js'
                ),
        },
        {
            path: '/customerJourney',
            name: 'CustomerJourney',
            meta: {
                requireLogin: true,
                toTopVisible: false,
                canChangeAccount: true,
            },
            component: () =>
                import(
                    /* webpackChunkName: "customerJourney" */ '@/views/ReactCustomerJourney.js'
                ),
        },
    ],
});

const USER = 'user/user';

// Plan A sign-ups are closed (TASK-14124): rewrite `planType=A` on the sign
// routes to T before the other guards and the sign-up views read it.
router.beforeEach((to, from, next) => {
    if (!isSignRoute(to.path)) {
        return next();
    }
    const query = normalizeSignupQuery(to.query);
    if (query !== to.query) {
        return next({ path: to.path, query, hash: to.hash, replace: true });
    }
    next();
});

router.beforeEach((from, to, next) => {
    const token = getAdrielJwt();

    if (token) {
        setAdrielJwt(token);
        AuthService.setAccessToken$.next(token);
    }

    next();
});

//login check hook
router.beforeEach((to, from, next) => {
    window.scrollTo(0, 0);
    var getters = store.getters;

    const user = getters[USER];

    !R.isNil(user) && setUtm();

    //if login user exist or already failed to login pass to next
    if (user || getters['user/authFailed'] === true) {
        return next();
    }

    if (R.pathEq(['meta', 'fetchUser'], false, to)) {
        return next();
    }

    fromPromise(store.dispatch('user/checkSession'))
        .pipe(timeout(5000))
        .subscribe(
            () => {
                if (
                    ['Sign/Login', 'Sign/Join'].includes(to.name) &&
                    getters[USER] &&
                    from.name !== to.name
                ) {
                    next({ name: 'MyCampaigns' });
                } else {
                    next();
                }
            },
            (err) => {
                console.log(err);
                next();
            }
        );
});

var requireLogin = (name) => {
    return new Set([
        'Proposal',
        'Proposal/Ads',
        'Proposal/Boost',
        'Proposal/Plan',
        'Proposal/AudienceProfile',
        'Proposal/AudienceJourney',
        'Dashboard',
        'OldDashboard',
        'reactDashboard',
        // 'NewDashboard',
        'MyCampaigns',
        'Settings',
        'Express',
        'Onboarding',
        'Manager',
        'Connection',
        'Consulting',
        'Building',
        'Auth',
        'CreateWorkspace',
        'ConnectionSourceRefreshHistory',
        'AgentChat',
    ]).has(name);
};

router.beforeEach((to, from, next) => {
    const fromCsm = R.includes(R.prop('name', to), ['Building', 'Consulting']);
    const name = fromCsm ? to.name || from.name : from.name || to.name;
    let query = {
        ...to.query,
        ...from.query,
        dest: name,
    };

    const hasPPKey = !!query.ppkey;

    const user = store.getters[USER];
    const isAllowedToAccess = !requireLogin(name) || !!user;

    if (hasPPKey) {
        localStorage.setItem(PP_KEY, query.ppkey);

        return isAllowedToAccess
            ? next({ name: 'Express' })
            : next({
                  name: 'Sign/Join',
                  query: {
                      planType: 'T',
                      lang: 'ko',
                  },
              });
    }

    const fromName = from.name;
    const toName = to.name;

    const headToEmailVerify = R.includes(toName, ['Auth', 'Verify']);
    const userExist = isValueExist(user);
    const comeFromLogin = fromName === 'Sign/Login';

    const emailVerifyAllowed =
        headToEmailVerify && (userExist || comeFromLogin);
    const isVerified = R.prop('email_verified', user);

    if (emailVerifyAllowed || !requireLogin(toName)) {
        return next();
    }

    // if users are not verified then move to 'Auth'
    return isAllowedToAccess
        ? isVerified
            ? next()
            : next({ name: 'Auth' })
        : next(`/sign/login?${stringify(query)}`);
});

router.beforeEach((to, from, next) => {
    const QUERY_KEY = 'accountId';
    const path = R.path(['query', QUERY_KEY]);

    const newId = path(to);
    const oldId = path(from) || 0;

    if (newId === undefined || (newId === 0 && oldId)) {
        const query = R.assoc(QUERY_KEY, oldId, to.query);

        axios.defaults.headers.common.AccountId = oldId;

        next({
            path: to.path,
            query,
        });
    } else {
        next();
    }
});

// load font icons once
// except for Dashboard page
router.beforeEach((to, from, next) => {
    to.name !== 'Dashboard' && loadIconCss();

    next();
});

router.onError(({ message } = {}) => {
    if (
        process.env.VUE_APP_STATUS === 'app' &&
        /chunk .* fail/gim.test(message) &&
        window.confirm(
            'There is a new version of this page available. Please refresh the page.'
        )
    ) {
        window.location.reload();
    }
});

//emergency
// const emergencyRouter = new Router({
//     routes: [
//         { path: '*', redirect: '/503' },
//         {
//             path: '/503',
//             component: () =>
//                 import(
//                     /* webpackChunkName: "maintenance" */ '@/views/EmergencyPage'
//                 ),
//             name: 'EmergencyPage',
//         },
//     ],
// });

export default router; // router / emergencyRouter
// export default emergencyRouter;

const setUtm = R.once(() => {
    if (window.location.href.indexOf('?&_ga=') > -1) {
        window.ga && window.ga('set', 'referrer', window.location.href);
    }
});
